<template>
  <div class="kpi-card" :style="cardStyle">
    <div class="kpi-card-head">
      <div>
        <div class="kpi-card-title">{{ title }}</div>
      </div>
      <div class="kpi-card-badge">{{ normalizedValue }}%</div>
    </div>
    <div class="kpi-card-body">
      <div
        class="kpi-card-pie"
        :data-value="`${normalizedValue}%`"
        :style="{ background: getBackgroundColor }"
      >
      </div>
      <div class="kpi-card-meta">
        <div class="kpi-card-value">{{ normalizedValue }}%</div>
        <div class="kpi-card-caption">{{ progressLabel }}</div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'kpiCard',
  props: {
    title: {
      type: String,
      default: ''
    },
    value: {
      type: [Number, String],
      default: 88.88
    },
    color: {
      type: String,
      default: '#00b0ff'
    }
  },
  computed: {
    normalizedValue () {
      const parsedValue = Number(this.value)
      if (Number.isNaN(parsedValue)) return '0.00'
      return Math.max(0, Math.min(100, parsedValue)).toFixed(2)
    },
    progressLabel () {
      const value = Number(this.normalizedValue)
      if (value >= 75) return 'Strong match'
      if (value >= 40) return 'Moderate change'
      if (value > 0) return 'Needs review'
      return 'No change detected'
    },
    cardStyle () {
      return {
        '--kpi-color': this.color,
        '--kpi-glow': `${this.color}22`,
        '--kpi-soft': `${this.color}14`
      }
    },
    getBackgroundColor () {
      return `conic-gradient(var(--kpi-color) 0% ${this.normalizedValue}%, #ebe5de ${this.normalizedValue}% 100%)`
    }
  }
}
</script>

<style lang="scss" scoped >
.kpi-card {
  position: relative;
  overflow: hidden;
  padding: 12px 14px;
  min-height: 132px;
  margin: 0;
  border-radius: 10px;
  border: 1px solid rgba(56, 51, 50, 0.08);
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 3px 10px rgba(56, 51, 50, 0.04);
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: space-between;
  transition: box-shadow 0.18s ease, border-color 0.18s ease;

  &:hover {
    border-color: var(--kpi-glow);
    box-shadow: 0 5px 12px rgba(56, 51, 50, 0.05);
  }

  &-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
  }

  &-title {
    font-size: var(--text-m);
    line-height: 1.2;
    font-weight: 500;
    color: var(--text-color-1);
    max-width: 11ch;
  }

  &-badge {
    flex-shrink: 0;
    padding: 4px 8px;
    border-radius: 999px;
    font-size: var(--text-xs3);
    font-weight: 600;
    color: var(--text-color-1);
    background: transparent;
    border: 1px solid var(--kpi-glow);
  }

  &-body {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &-meta {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &-value {
    font-size: var(--text-l);
    font-weight: 500;
    color: var(--text-color-1);
    line-height: 1;
  }

  &-caption {
    font-size: var(--text-xs2);
    color: rgba(56, 51, 50, 0.58);
  }

  &-pie {
    flex-shrink: 0;
    font-size: var(--text-xs);
    width: 4.6em;
    height: 4.6em;
    border-radius: 50%;
    position: relative;
    box-shadow: inset 0 0 0 1px rgba(56, 51, 50, 0.04);

    &::before {
      content: "";
      display: block;
      position: absolute;
      width: 3.35em;
      height: 3.35em;
      top: 0.62em;
      left: 0.62em;
      border-radius: 50%;
      background: #fffdfa;
      box-shadow: inset 0 0 0 1px rgba(56, 51, 50, 0.05);
    }

    &::after {
      content: attr(data-value);
      font-size: 0.72em;
      font-weight: 600;
      color: var(--text-color-1);
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }
}

@media (max-width: 1200px) {
  .kpi-card {
    min-height: 126px;

    &-title {
      max-width: none;
    }
  }
}
</style>
